import React from "react";
import { Helmet } from "react-helmet-async";
import { COMPANY_DATA } from "../../constant/CompanyData";
import Eyebrow from "../common/Eyebrow";

const CookiePolicy = () => {
  return (
    <div className="bg-slate-50 py-16 px-6 sm:px-12 md:px-24">
      <Helmet>
        <title>Cookie Policy - QuantaRock</title>
        <meta
          name="description"
          content="How QuantaRock Systems Pvt Ltd uses cookies and similar technologies on its website."
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://quantarock.in/legal/cookies" />
      </Helmet>

      <div className="mx-auto max-w-4xl">
        <div className="text-center">
          <Eyebrow>Legal</Eyebrow>
        </div>
        <h1 className="mt-3 text-center text-4xl font-bold tracking-tight text-[var(--website-primary-color)]">
          Cookie Policy
        </h1>

        <div className="mt-10 space-y-8 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm md:p-12">
          <section>
            <h2 className="text-xl font-semibold text-[var(--website-primary-color)]">
              1. Introduction
            </h2>
            <p className="mt-2 leading-relaxed text-slate-600">
              This Cookie Policy explains how QuantaRock Systems Pvt Ltd uses
              cookies and similar technologies to recognize you when you
              visit our website. It also explains what cookies are, why we
              use them, and your choices regarding cookies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[var(--website-primary-color)]">
              2. What Are Cookies?
            </h2>
            <p className="mt-2 leading-relaxed text-slate-600">
              Cookies are small text files that are stored on your device
              when you visit a website. They allow the website to recognize
              your device and store some information about your preferences
              or actions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[var(--website-primary-color)]">
              3. How We Use Cookies
            </h2>
            <p className="mt-2 leading-relaxed text-slate-600">
              We use cookies for several purposes, including:
            </p>
            <ul className="mt-3 list-inside list-disc space-y-1.5 text-slate-600">
              <li>To enhance the functionality and performance of our website.</li>
              <li>To personalize your experience on our website.</li>
              <li>
                To analyze how our website is used and improve its
                performance.
              </li>
              <li>To power our live chat widget so it remembers your session.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[var(--website-primary-color)]">
              4. Cookies We Actually Use
            </h2>
            <p className="mt-2 leading-relaxed text-slate-600">
              We keep this list to exactly what our site sets — no generic
              placeholder categories. Analytics cookies are only set if you
              choose "Accept All" in our cookie banner; necessary cookies run
              regardless, since the site can't function without them.
            </p>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[640px] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500">
                    <th className="py-2 pr-4 font-semibold">Cookie</th>
                    <th className="py-2 pr-4 font-semibold">Provider</th>
                    <th className="py-2 pr-4 font-semibold">Purpose</th>
                    <th className="py-2 pr-4 font-semibold">Category</th>
                    <th className="py-2 font-semibold">Retention</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-600">
                  <tr>
                    <td className="py-2 pr-4 font-mono text-xs">_ga</td>
                    <td className="py-2 pr-4">Google Analytics</td>
                    <td className="py-2 pr-4">Distinguishes unique visitors</td>
                    <td className="py-2 pr-4">Analytics (opt-in)</td>
                    <td className="py-2">2 years</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-mono text-xs">_ga_&lt;container-id&gt;</td>
                    <td className="py-2 pr-4">Google Analytics</td>
                    <td className="py-2 pr-4">Tracks session state</td>
                    <td className="py-2 pr-4">Analytics (opt-in)</td>
                    <td className="py-2">2 years</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-mono text-xs">_clck</td>
                    <td className="py-2 pr-4">Microsoft Clarity</td>
                    <td className="py-2 pr-4">Persists a user ID across visits</td>
                    <td className="py-2 pr-4">Analytics (opt-in)</td>
                    <td className="py-2">1 year</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-mono text-xs">_clsk</td>
                    <td className="py-2 pr-4">Microsoft Clarity</td>
                    <td className="py-2 pr-4">Links pageviews into one session recording</td>
                    <td className="py-2 pr-4">Analytics (opt-in)</td>
                    <td className="py-2">1 day</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-mono text-xs">CLID, MUID, ANONCHK, MR, SM</td>
                    <td className="py-2 pr-4">Microsoft Clarity</td>
                    <td className="py-2 pr-4">Operational identifiers used by Clarity/Microsoft</td>
                    <td className="py-2 pr-4">Analytics (opt-in)</td>
                    <td className="py-2">Minutes – 1 year</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4 font-mono text-xs">TawkConnectionTime, twk_*</td>
                    <td className="py-2 pr-4">Tawk.to (live chat)</td>
                    <td className="py-2 pr-4">Keeps your chat session connected and in sync</td>
                    <td className="py-2 pr-4">Necessary</td>
                    <td className="py-2">Session</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 leading-relaxed text-slate-600">
              Our Trustpilot review widget does not set any cookies of its own — it
              runs as an embedded frame and only reads cookies already present if
              you've separately visited Trustpilot's own site.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[var(--website-primary-color)]">
              5. Your Cookie Choices
            </h2>
            <p className="mt-2 leading-relaxed text-slate-600">
              When you first visit our site, a cookie banner lets you choose
              "Accept All" (necessary + analytics cookies) or "Necessary
              Only." You can change your mind at any time using the "Cookie
              Preferences" link in our footer. You can also control cookies
              through your browser settings, though blocking necessary
              cookies may affect how well the site works.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-[var(--website-primary-color)]">
              6. Updates to This Cookie Policy
            </h2>
            <p className="mt-2 leading-relaxed text-slate-600">
              We may update this Cookie Policy from time to time. When we do,
              we will post the updated policy on this page. Please review
              this policy regularly to stay informed about how we use
              cookies.
            </p>
          </section>
        </div>

        <p className="mt-8 text-center text-sm text-slate-500">
          Copyright &copy; {new Date().getFullYear()} — All rights reserved
          by {COMPANY_DATA.companyName}
        </p>
      </div>
    </div>
  );
};

export default CookiePolicy;
